import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPapers,
  fetchPaperSummary,
  fetchSubjects,
  fetchStudentPaperSummary,
} from "../../redux/actions/attendanceActions";
import {
  FiCalendar,
  FiFilter,
  FiSearch,
  FiDownload,
  FiPrinter,
  FiUser,
  FiBookOpen,
  FiTrendingUp,
  FiTrendingDown,
  FiCheckCircle,
  FiXCircle,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiX,
  FiChevronDown,
  FiBook,
  FiAlertCircle,
} from "react-icons/fi";
import { MdPerson, MdPhone } from "react-icons/md";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx";

const PaperWiseReports = () => {
  const dispatch = useDispatch();

  // State for filters
  const [subjectId, setSubjectId] = useState("");
  const [paperId, setPaperId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [classType, setClassType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [paperSearch, setPaperSearch] = useState("");
  const [subjectSearch, setSubjectSearch] = useState("");
  const [showPaperDropdown, setShowPaperDropdown] = useState(false);
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);

  // Search state for student search
  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const [debouncedStudentSearchTerm, setDebouncedStudentSearchTerm] =
    useState("");

  const [showStudentModal, setShowStudentModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  // Refs
  const paperDropdownRef = useRef(null);
  const paperSelectRef = useRef(null);
  const subjectDropdownRef = useRef(null);
  const subjectSelectRef = useRef(null);

  const debounceTimeoutRef = useRef(null);

  // Get data from Redux
  const {
    paperSummary,
    papers,
    subjects,
    loading,
    error,
    studentPaperSummary,
  } = useSelector((state) => state.attendance);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedStudentSearchTerm(studentSearchTerm);
    }, 300);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [studentSearchTerm]);

  const filteredStudents = useMemo(() => {
    const students = paperSummary?.students || [];

    if (!debouncedStudentSearchTerm.trim()) return students;

    const searchLower = debouncedStudentSearchTerm.toLowerCase().trim();

    return students.filter((student) => {
      const studentNameMatch =
        student.student_name?.toLowerCase().includes(searchLower) || false;
      const studentIdMatch =
        student.student_id?.toString().includes(searchLower) || false;

      return studentNameMatch || studentIdMatch;
    });
  }, [paperSummary?.students, debouncedStudentSearchTerm]);

  const filteredPagination = useMemo(() => {
    if (!filteredStudents || filteredStudents.length === 0) {
      return { total: 0, totalPages: 0 };
    }
    return {
      total: filteredStudents.length,
      totalPages: Math.ceil(filteredStudents.length / limit),
    };
  }, [filteredStudents, limit]);

  const currentFilteredStudents = useMemo(() => {
    if (!filteredStudents || filteredStudents.length === 0) return [];
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;
    return filteredStudents.slice(startIndex, endIndex);
  }, [filteredStudents, currentPage, limit]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedStudentSearchTerm]);

  useEffect(() => {
    setStudentSearchTerm("");
    setDebouncedStudentSearchTerm("");
    setCurrentPage(1);
  }, [paperId]);

  // Fetch subjects on component mount
  useEffect(() => {
    dispatch(fetchSubjects());
  }, [dispatch]);

  // Fetch papers when subject changes
  useEffect(() => {
    if (subjectId) {
      dispatch(fetchPapers(subjectId));
      setPaperId("");
    } else {
      dispatch(fetchPapers());
    }
  }, [dispatch, subjectId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        paperDropdownRef.current &&
        !paperDropdownRef.current.contains(event.target) &&
        paperSelectRef.current &&
        !paperSelectRef.current.contains(event.target)
      ) {
        setShowPaperDropdown(false);
      }
      if (
        subjectDropdownRef.current &&
        !subjectDropdownRef.current.contains(event.target) &&
        subjectSelectRef.current &&
        !subjectSelectRef.current.contains(event.target)
      ) {
        setShowSubjectDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredPapers =
    papers?.filter(
      (paper) =>
        paper.paper_name.toLowerCase().includes(paperSearch.toLowerCase()) ||
        paper.paper_type?.toLowerCase().includes(paperSearch.toLowerCase()) ||
        paper.id.toString().includes(paperSearch),
    ) || [];

  const filteredSubjects =
    subjects?.filter(
      (subject) =>
        subject.subject_name
          .toLowerCase()
          .includes(subjectSearch.toLowerCase()) ||
        subject.id.toString().includes(subjectSearch),
    ) || [];

  const handleSubjectSelect = (subject) => {
    setSubjectId(subject.id);
    setShowSubjectDropdown(false);
    setSubjectSearch("");
  };

  const handlePaperSelect = (paper) => {
    setPaperId(paper.id);
    setShowPaperDropdown(false);
    setPaperSearch("");
  };

  const handleClearSubject = () => {
    setSubjectId("");
    setSubjectSearch("");
  };

  const handleClearPaper = () => {
    setPaperId("");
    setPaperSearch("");
  };

  const handleSearch = () => {
    if (!paperId) {
      toast.error("Please select a paper");
      return;
    }

    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      toast.error("From date cannot be after to date");
      return;
    }

    setCurrentPage(1);
    setStudentSearchTerm("");
    setDebouncedStudentSearchTerm("");
    dispatch(
      fetchPaperSummary({
        paper_id: paperId,
        fromDate,
        toDate,
        class_type: classType,
        page: 1,
        limit,
      }),
    );
  };

  // Handle reset
  const handleReset = () => {
    setSubjectId("");
    setPaperId("");
    setFromDate("");
    setToDate("");
    setClassType("");
    setCurrentPage(1);
    setPaperSearch("");
    setSubjectSearch("");
    setStudentSearchTerm("");
    setDebouncedStudentSearchTerm("");
    setShowPaperDropdown(false);
    setShowSubjectDropdown(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > (filteredPagination?.totalPages || 1)) return;
    setCurrentPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  const handleExportToExcel = () => {
    if (!paperId || !filteredStudents.length) {
      toast.error("No data to export");
      return;
    }

    try {
      // Prepare data for export
      const exportData = filteredStudents.map((student) => ({
        "Student ID": student.student_id,
        "Student Name": student.student_name,
        "Total Classes": student.total_class,
        Present: student.present,
        Absent: student.absent,
        "Attendance Percentage": student.present_percentage,
        Status: getAttendanceStatus(student.present_percentage).label,
        Paper: paperSummary.paperName || "N/A",
        Teacher: paperSummary.teacherName || "N/A",
      }));

      const stats = getAttendanceStats();
      if (stats) {
        exportData.push(
          {},
          {
            "Student Name": "SUMMARY",
            "Total Students": stats.totalStudents,
            "Total Classes": paperSummary.summary?.total_class || 0,
            "Overall Attendance": stats.avgAttendance,
            "Perfect Attendance": stats.perfectAttendance,
            "Low Attendance (<75%)": stats.lowAttendance,
            Period: `${paperSummary.summary?.from_date || "N/A"} to ${paperSummary.summary?.to_date || "N/A"}`,
          },
        );
      }

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, `Paper Attendance`);

      // Generate file name
      const fileName = `Paper_${paperId}_Attendance_Report_${new Date().toISOString().split("T")[0]}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, fileName);

      toast.success(`Report exported successfully as ${fileName}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export report");
    }
  };

  // Export to CSV (using filtered data)
  const handleExportToCSV = () => {
    if (!paperId || !filteredStudents.length) {
      toast.error("No data to export");
      return;
    }

    try {
      // Prepare data for CSV
      const headers = [
        "Student ID,Student Name,Total Classes,Present,Absent,Attendance Percentage,Status,Paper,Teacher",
      ];

      const rows = filteredStudents.map((student) => {
        const status = getAttendanceStatus(student.present_percentage).label;
        return `${student.student_id},"${student.student_name}",${student.total_class},${student.present},${student.absent},${student.present_percentage},${status},"${paperSummary.paperName || "N/A"}","${paperSummary.teacherName || "N/A"}"`;
      });

      // Add summary
      const stats = getAttendanceStats();
      if (stats) {
        rows.push(""); // Empty line
        rows.push('"SUMMARY",,,,,'); // Summary header
        rows.push(`"Total Students",${stats.totalStudents}`);
        rows.push(`"Total Classes",${paperSummary.summary?.total_class || 0}`);
        rows.push(`"Overall Attendance","${stats.avgAttendance}"`);
        rows.push(`"Perfect Attendance",${stats.perfectAttendance}`);
        rows.push(`"Low Attendance (<75%)",${stats.lowAttendance}`);
        rows.push(
          `"Period","${paperSummary.summary?.from_date || "N/A"} to ${paperSummary.summary?.to_date || "N/A"}"`,
        );
      }

      const csvContent = [...headers, ...rows].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `Paper_${paperId}_Attendance_Report_${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Report exported successfully as CSV`);
    } catch (error) {
      console.error("CSV export error:", error);
      toast.error("Failed to export CSV report");
    }
  };

  // Handle print
  const handlePrint = () => {
    if (!paperId) {
      toast.error("Please select a paper first");
      return;
    }
    window.print();
  };
  // Handle view student details
  const handleViewStudent = (studentId, studentName) => {
    if (!paperId) {
      toast.error("No paper selected");
      return;
    }

    setSelectedStudent({
      id: studentId,
      name: studentName,
    });
    setShowStudentModal(true);

    // Fetch student paper summary
    dispatch(
      fetchStudentPaperSummary({
        student_id: studentId,
        paper_id: paperId,
      }),
    );
  };

  // Get attendance status
  const getAttendanceStatus = (percentage) => {
    const percent = parseFloat(percentage);
    if (percent >= 75)
      return {
        label: "Good",
        color: "green",
        bg: "bg-green-100 dark:bg-green-900/30",
        text: "text-green-800 dark:text-green-300",
      };
    if (percent >= 50)
      return {
        label: "Average",
        color: "yellow",
        bg: "bg-yellow-100 dark:bg-yellow-900/30",
        text: "text-yellow-800 dark:text-yellow-300",
      };
    return {
      label: "Poor",
      color: "red",
      bg: "bg-red-100 dark:bg-red-900/30",
      text: "text-red-800 dark:text-red-300",
    };
  };

  // Calculate attendance statistics from filtered data
  const getAttendanceStats = () => {
    if (!filteredStudents.length) return null;

    const totalStudents = filteredStudents.length;
    const perfectAttendance = filteredStudents.filter(
      (s) => s.present_percentage === "100.00%",
    ).length;
    const lowAttendance = filteredStudents.filter(
      (s) => parseFloat(s.present_percentage) < 75,
    ).length;
    const avgAttendance =
      filteredStudents.reduce(
        (sum, s) => sum + parseFloat(s.present_percentage),
        0,
      ) / totalStudents;

    return {
      totalStudents,
      perfectAttendance,
      lowAttendance,
      avgAttendance: avgAttendance.toFixed(2) + "%",
    };
  };

  const stats = getAttendanceStats();
  const pagination = filteredPagination;
  const students = currentFilteredStudents;

  // Get selected paper and subject names
  const selectedPaper = papers?.find((paper) => paper.id === paperId);
  const selectedSubject = subjects?.find((subject) => subject.id === subjectId);

  // Helper function to get paper display name
  const getPaperDisplayName = (paper) => {
    if (paper.paper_type) {
      return `${paper.paper_name} (${paper.paper_type})`;
    }
    return paper.paper_name;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            Paper Wise Attendance Report
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Analyze attendance data paper-wise with detailed insights
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Export Dropdown */}
          <div className="relative group">
            <button
              disabled={!paperId || !filteredStudents.length}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-lg ${
                paperId && filteredStudents.length
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              }`}
            >
              <FiDownload className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-20">
              <button
                onClick={handleExportToExcel}
                disabled={!paperId || !filteredStudents.length}
                className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-2 text-gray-700 dark:text-gray-300"
              >
                <FiFileText className="w-4 h-4" />
                Export as Excel
              </button>
              <button
                onClick={handleExportToCSV}
                disabled={!paperId || !filteredStudents.length}
                className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-2 text-gray-700 dark:text-gray-300"
              >
                <FiFileText className="w-4 h-4" />
                Export as CSV
              </button>
            </div>
          </div>

          <button
            onClick={handlePrint}
            disabled={!paperId}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-lg ${
              paperId
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
            }`}
          >
            <FiPrinter className="w-4 h-4" />
            <span className="hidden sm:inline">Print</span>
          </button>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3 mb-6">
          <FiFilter className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Filter Options
          </h2>
        </div>

        {/* Row 1: Subject and Paper */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Subject Select - Custom Dropdown with Search */}
          <div className="relative" ref={subjectSelectRef}>
            <div className="relative group">
              <div className="absolute left-4 top-4 text-gray-400">
                <FiBook className="w-5 h-5" />
              </div>
              <button
                onClick={() => setShowSubjectDropdown(!showSubjectDropdown)}
                className="w-full flex items-center justify-between pl-12 pr-4 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition-all duration-300 group-hover:border-purple-400 text-left"
              >
                <span className="truncate">
                  {selectedSubject
                    ? selectedSubject.subject_name
                    : "Select Subject"}
                </span>
                <div className="flex items-center gap-2">
                  {subjectId && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearSubject();
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  )}
                  <FiChevronDown
                    className={`w-5 h-5 transition-transform duration-300 ${showSubjectDropdown ? "rotate-180" : ""}`}
                  />
                </div>
              </button>
            </div>

            {/* Subject Dropdown with Search - Keep the same */}
            {showSubjectDropdown && (
              <div
                ref={subjectDropdownRef}
                className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-h-96 overflow-hidden"
              >
                {/* Search Input */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search subject by name or ID..."
                      className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                      value={subjectSearch}
                      onChange={(e) => setSubjectSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                </div>

                {/* Subject List */}
                <div className="overflow-y-auto max-h-64">
                  {filteredSubjects.length > 0 ? (
                    <div className="py-1">
                      {filteredSubjects.map((subject) => (
                        <button
                          key={subject.id}
                          onClick={() => handleSubjectSelect(subject)}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 ${
                            subjectId === subject.id
                              ? "bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300"
                              : "text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          <div className="font-medium truncate">
                            {subject.subject_name}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <FiSearch className="w-8 h-8 text-gray-400 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-500 dark:text-gray-400">
                        {subjectSearch
                          ? "No subjects found matching your search"
                          : "No subjects available"}
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {filteredSubjects.length} of {subjects?.length || 0}{" "}
                    subjects
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Paper Select - Custom Dropdown with Search */}
          <div className="relative" ref={paperSelectRef}>
            <div className="relative group">
              <div className="absolute left-4 top-4 text-gray-400">
                <FiBookOpen className="w-5 h-5" />
              </div>
              <button
                onClick={() => setShowPaperDropdown(!showPaperDropdown)}
                className="w-full flex items-center justify-between pl-12 pr-4 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition-all duration-300 group-hover:border-purple-400 text-left"
              >
                <span className="truncate">
                  {selectedPaper
                    ? getPaperDisplayName(selectedPaper)
                    : "Select Paper"}
                </span>
                <div className="flex items-center gap-2">
                  {paperId && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearPaper();
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  )}
                  <FiChevronDown
                    className={`w-5 h-5 transition-transform duration-300 ${showPaperDropdown ? "rotate-180" : ""}`}
                  />
                </div>
              </button>
            </div>

            {/* Paper Dropdown with Search - Keep the same */}
            {showPaperDropdown && (
              <div
                ref={paperDropdownRef}
                className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-h-96 overflow-hidden"
              >
                {/* Search Input */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search by paper name or type..."
                      className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                      value={paperSearch}
                      onChange={(e) => setPaperSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                </div>

                {/* Paper List */}
                <div className="overflow-y-auto max-h-64">
                  {filteredPapers.length > 0 ? (
                    <div className="py-1">
                      {filteredPapers.map((paper) => (
                        <button
                          key={paper.id}
                          onClick={() => handlePaperSelect(paper)}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 ${
                            paperId === paper.id
                              ? "bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300"
                              : "text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          <div className="font-medium truncate">
                            {paper.paper_name}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {paper.paper_type && (
                              <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full">
                                {paper.paper_type}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <FiSearch className="w-8 h-8 text-gray-400 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-500 dark:text-gray-400">
                        {paperSearch
                          ? "No papers found matching your search"
                          : "No papers available"}
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {filteredPapers.length} of {papers?.length || 0} papers
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Date, Class Type, and Actions */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* From Date */}
          <div className="relative group">
            <div className="absolute left-4 top-4 text-gray-400">
              <FiCalendar className="w-5 h-5" />
            </div>
            <input
              type="date"
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition-all duration-300 group-hover:border-purple-400"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>

          {/* To Date */}
          <div className="relative group">
            <div className="absolute left-4 top-4 text-gray-400">
              <FiCalendar className="w-5 h-5" />
            </div>
            <input
              type="date"
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition-all duration-300 group-hover:border-purple-400"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>

          {/* Class Type Filter */}
          <div className="relative group">
            <div className="absolute left-4 top-4 text-gray-400">
              <FiBook className="w-5 h-5" />
            </div>
            <select
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition-all duration-300 group-hover:border-purple-400"
              value={classType}
              onChange={(e) => setClassType(e.target.value)}
            >
              <option value="">All Class Types</option>
              <option value="theory">Theory</option>
              <option value="practical">Practical</option>
              <option value="tutorial">Tutorial</option>
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 md:col-span-2">
            <button
              onClick={handleSearch}
              disabled={loading || !paperId}
              className={`flex-1 flex items-center justify-center gap-2 font-semibold py-3 px-6 rounded-xl transition-all duration-300 hover:shadow-lg ${
                paperId
                  ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              }`}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <FiSearch className="w-5 h-5" />
                  <span>Search</span>
                </>
              )}
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium rounded-xl transition-all duration-300"
            >
              Reset
            </button>
          </div>
        </div>
      </div>
      {/* Search Bar */}
      {paperId && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="relative group">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <FiSearch className="w-5 h-5" />
            </div>
            <input
              type="text"
              placeholder="Search by Student Name or ID..."
              value={studentSearchTerm}
              onChange={(e) => setStudentSearchTerm(e.target.value)}
              className="w-full pl-12 pr-12 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition-all duration-300 group-hover:border-purple-400 text-base"
              autoComplete="off"
            />
            {studentSearchTerm && (
              <button
                onClick={() => {
                  setStudentSearchTerm("");
                  setDebouncedStudentSearchTerm("");
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            )}
          </div>
          {debouncedStudentSearchTerm &&
            filteredStudents.length === 0 &&
            paperSummary?.students?.length > 0 && (
              <p className="mt-2 text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <FiAlertCircle className="w-4 h-4" />
                No students found matching "{debouncedStudentSearchTerm}"
              </p>
            )}
          {debouncedStudentSearchTerm && filteredStudents.length > 0 && (
            <p className="mt-2 text-sm text-green-600 dark:text-green-400">
              Found {filteredStudents.length} student
              {filteredStudents.length !== 1 ? "s" : ""} matching "
              {debouncedStudentSearchTerm}"
            </p>
          )}
        </div>
      )}
      {/* Paper Info Card */}
      {paperSummary?.paperName && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl shadow-lg p-6 border border-purple-200 dark:border-purple-800">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="space-y-2">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {paperSummary.paperName}
              </h3>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <MdPerson className="w-4 h-4" />
                  <span>Teacher: {paperSummary.teacherName}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <MdPhone className="w-4 h-4" />
                  <span>Contact: {paperSummary.teacherContact}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <FiCalendar className="w-4 h-4" />
                  <span>
                    Period: {paperSummary.summary?.from_date} to{" "}
                    {paperSummary.summary?.to_date}
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {paperSummary.summary?.total_class || 0}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Total Classes
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards - Using filtered data */}
      {paperId && stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Students
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {stats.totalStudents}
                </p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                <FiUser className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Perfect Attendance
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                  {stats.perfectAttendance}
                </p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                <FiCheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Low Attendance
                </p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
                  {stats.lowAttendance}
                </p>
              </div>
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                <FiXCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Average Attendance
                </p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                  {stats.avgAttendance}
                </p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                <FiTrendingUp className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
        {/* Table Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Student Attendance Details
              </h3>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Showing {students.length} of {pagination.total} records
              </div>
            </div>

            {/* Pagination Controls - Top */}
            {pagination.totalPages > 0 && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Show
                  </span>
                  <select
                    value={limit}
                    onChange={(e) => handleLimitChange(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="500">500</option>
                    <option value="1000">1000</option>
                    <option value="2000">2000</option>
                  </select>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    entries
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="py-4 px-6 text-left text-sm font-semibold text-gray-900 dark:text-white">
                  Student Name
                </th>
                <th className="py-4 px-6 text-center text-sm font-semibold text-gray-900 dark:text-white">
                  Total Classes
                </th>
                <th className="py-4 px-6 text-center text-sm font-semibold text-gray-900 dark:text-white">
                  Present
                </th>
                <th className="py-4 px-6 text-center text-sm font-semibold text-gray-900 dark:text-white">
                  Absent
                </th>
                <th className="py-4 px-6 text-center text-sm font-semibold text-gray-900 dark:text-white">
                  Attendance %
                </th>
                <th className="py-4 px-6 text-center text-sm font-semibold text-gray-900 dark:text-white">
                  Class Type
                </th>
                <th className="py-4 px-6 text-center text-sm font-semibold text-gray-900 dark:text-white">
                  Status
                </th>
                <th className="py-4 px-6 text-center text-sm font-semibold text-gray-900 dark:text-white">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="8" className="py-12 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="w-12 h-12 border-4 border-purple-200 dark:border-purple-800 border-t-purple-600 dark:border-t-purple-400 rounded-full animate-spin"></div>
                      <p className="text-gray-600 dark:text-gray-400">
                        Loading attendance data...
                      </p>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="8" className="py-12 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-full">
                        <FiXCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                      </div>
                      <p className="text-red-600 dark:text-red-400 font-medium">
                        {error}
                      </p>
                      <button
                        onClick={handleSearch}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-300"
                      >
                        Try Again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : !paperId ? (
                <tr>
                  <td colSpan="8" className="py-16 text-center">
                    <div className="flex flex-col items-center justify-center gap-4">
                      <div className="p-4 bg-purple-100 dark:bg-purple-900/20 rounded-full">
                        <FiBookOpen className="w-12 h-12 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <p className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                          Select a Paper
                        </p>
                        <p className="text-gray-600 dark:text-gray-400">
                          Please select a paper and click "Search" to view
                          attendance data
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : filteredStudents.length > 0 ? (
                filteredStudents.map((row, index) => {
                  const status = getAttendanceStatus(row.present_percentage);

                  return (
                    <tr
                      key={row.student_id || index}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-200"
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-full flex items-center justify-center">
                            <FiUser className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {row.student_name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              ID: {row.student_id}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm font-medium">
                          {row.total_class}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <FiCheckCircle className="w-4 h-4 text-green-500" />
                          <span className="font-medium text-gray-900 dark:text-white">
                            {row.present}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <FiXCircle className="w-4 h-4 text-red-500" />
                          <span className="font-medium text-gray-900 dark:text-white">
                            {row.absent}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span
                            className={`text-lg font-bold ${
                              parseFloat(row.present_percentage) >= 75
                                ? "text-green-600 dark:text-green-400"
                                : parseFloat(row.present_percentage) >= 50
                                  ? "text-yellow-600 dark:text-yellow-400"
                                  : "text-red-600 dark:text-red-400"
                            }`}
                          >
                            {row.present_percentage}
                          </span>
                          <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                parseFloat(row.present_percentage) >= 75
                                  ? "bg-green-500"
                                  : parseFloat(row.present_percentage) >= 50
                                    ? "bg-yellow-500"
                                    : "bg-red-500"
                              }`}
                              style={{
                                width: `${Math.min(parseFloat(row.present_percentage), 100)}%`,
                              }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            paperSummary?.summary?.class_type === "theory"
                              ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                              : paperSummary?.summary?.class_type ===
                                  "practical"
                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                                : paperSummary?.summary?.class_type ===
                                    "tutorial"
                                  ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {paperSummary?.summary?.class_type
                            ? paperSummary.summary.class_type
                                .charAt(0)
                                .toUpperCase() +
                              paperSummary.summary.class_type.slice(1)
                            : "N/A"}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            parseFloat(row.present_percentage) >= 75
                              ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                              : parseFloat(row.present_percentage) >= 50
                                ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
                                : "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
                          }`}
                        >
                          {parseFloat(row.present_percentage) >= 75
                            ? "Good"
                            : parseFloat(row.present_percentage) >= 50
                              ? "Average"
                              : "Poor"}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <button
                          onClick={() =>
                            handleViewStudent(row.student_id, row.student_name)
                          }
                          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded-lg transition-colors duration-300"
                        >
                          <FiEye className="w-4 h-4" />
                          <span>View</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="py-16 text-center">
                    <div className="flex flex-col items-center justify-center gap-4">
                      <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full">
                        <FiSearch className="w-12 h-12 text-gray-400 dark:text-gray-600" />
                      </div>
                      <div>
                        <p className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                          {debouncedStudentSearchTerm
                            ? "No matching students found"
                            : "No attendance records found"}
                        </p>
                        <p className="text-gray-600 dark:text-gray-400">
                          {debouncedStudentSearchTerm
                            ? `No students found matching "${debouncedStudentSearchTerm}"`
                            : "Try adjusting your filters or select a different paper"}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {pagination.totalPages > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing {(currentPage - 1) * limit + 1} to{" "}
                {Math.min(currentPage * limit, pagination.total || 0)} of{" "}
                {pagination.total || 0} records
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg transition ${
                    currentPage === 1
                      ? "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                      : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                  }`}
                >
                  <FiChevronLeft />
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {Array.from(
                    { length: Math.min(5, pagination.totalPages || 1) },
                    (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      if (pageNum < 1 || pageNum > pagination.totalPages)
                        return null;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-10 h-10 rounded-lg transition ${
                            currentPage === pageNum
                              ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg"
                              : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    },
                  )}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.totalPages}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg transition ${
                    currentPage === pagination.totalPages
                      ? "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                      : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                  }`}
                >
                  Next
                  <FiChevronRight />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Student Details Modal */}
      {showStudentModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
              onClick={() => setShowStudentModal(false)}
            ></div>

            {/* Modal */}
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between z-10">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Attendance Details
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {selectedStudent?.name} (ID: {selectedStudent?.id})
                  </p>
                </div>
                <button
                  onClick={() => setShowStudentModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <FiX className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="w-12 h-12 border-4 border-purple-200 dark:border-purple-800 border-t-purple-600 dark:border-t-purple-400 rounded-full animate-spin"></div>
                  </div>
                ) : error ? (
                  <div className="text-center py-12 text-red-600">{error}</div>
                ) : studentPaperSummary ? (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Total Classes
                        </p>
                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          {studentPaperSummary.studentData?.total_class || 0}
                        </p>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Present
                        </p>
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {studentPaperSummary.studentData?.present || 0}
                        </p>
                      </div>
                      <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Absent
                        </p>
                        <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                          {studentPaperSummary.studentData?.absent || 0}
                        </p>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Attendance %
                        </p>
                        <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                          {studentPaperSummary.studentData
                            ?.present_percentage || "0%"}
                        </p>
                      </div>
                    </div>

                    {/* Calendar View */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Attendance Calendar
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-gray-50 dark:bg-gray-900/50">
                              <th className="p-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                Date
                              </th>
                              <th className="p-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                Time
                              </th>
                              <th className="p-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                Period
                              </th>
                              <th className="p-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {studentPaperSummary.studentData?.attendance_details?.map(
                              (detail, index) => (
                                <tr
                                  key={index}
                                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                                >
                                  <td className="p-3 text-sm text-gray-700 dark:text-gray-300">
                                    {new Date(detail.date).toLocaleDateString(
                                      "en-IN",
                                      {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      },
                                    )}
                                  </td>
                                  <td className="p-3 text-sm text-gray-700 dark:text-gray-300">
                                    {detail.time}
                                  </td>
                                  <td className="p-3 text-sm text-gray-700 dark:text-gray-300">
                                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs">
                                      Period {detail.period}
                                    </span>
                                  </td>
                                  <td className="p-3">
                                    <span
                                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                                        detail.is_present
                                          ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                                          : "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
                                      }`}
                                    >
                                      {detail.is_present ? (
                                        <>
                                          <FiCheckCircle className="w-4 h-4" />
                                          Present
                                        </>
                                      ) : (
                                        <>
                                          <FiXCircle className="w-4 h-4" />
                                          Absent
                                        </>
                                      )}
                                    </span>
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    No attendance data available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaperWiseReports;
